import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import SvgIcon from "@mui/material/SvgIcon";
import TextField from "@mui/material/TextField";
import { Minus as MinusIcon, Plus as PlusIcon, Trash2 as TrashIcon } from "react-feather";

const styles = {
  quantityContainer: {
    display: "inline-flex",
    alignItems: "center",
    border: "1px solid",
    borderColor: "primary.main",
    borderRadius: "25px",
    overflow: "hidden",
    backgroundColor: "white",
    height: "36px",
  },
  quantityButtonMinus: {
    minWidth: "28px",
    height: "36px",
    border: "none",
    borderRadius: 0,
    color: "#ff6b35",
    "&:hover": {
      backgroundColor: "rgba(255, 107, 53, 0.08)",
      border: "none",
    },
    "& svg": {
      fontSize: "1rem",
    },
  },
  quantityButtonDelete: {
    minWidth: "28px",
    height: "36px",
    border: "none",
    borderRadius: 0,
    color: "#d32f2f",
    "&:hover": {
      backgroundColor: "rgba(211, 47, 47, 0.12)",
      border: "none",
    },
    "& svg": {
      fontSize: "1rem",
    },
  },
  quantityInput: {
    width: "44px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: "none",
      },
    },
    "& input": {
      fontSize: "1rem",
      fontWeight: 500,
      padding: "8px 4px",
      textAlign: "center",
      height: "20px",
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        WebkitAppearance: "none",
        margin: 0,
      },
      "&[type=number]": {
        MozAppearance: "textfield",
      },
    },
  },
  quantityButtonPlus: {
    minWidth: "28px",
    height: "36px",
    border: "none",
    borderRadius: 0,
    color: "#1976d2",
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.08)",
      border: "none",
    },
    "& svg": {
      fontSize: "1rem",
    },
  },
};

const QuantitySelector = ({
  quantity,
  onMinus,
  onPlus,
  onChange,
  onDelete,
  disabled = false,
  min = 0,
  inputReadOnly = false,
  onQuantityClick,
}) => {
  const showDeleteButton = onDelete && quantity === 1;
  const handleSelectCurrentValue = event => {
    const input = event.currentTarget;

    window.requestAnimationFrame(() => {
      if (document.activeElement === input) {
        input.select();
      }
    });
  };

  const handleMouseUp = event => {
    // Keep full selection after click; otherwise mouseup can collapse to a caret.
    event.preventDefault();
  };

  const handleChange = event => {
    if (inputReadOnly) return;

    // Keep the field numeric-only while still using a text input for reliable select().
    if (/^\d*$/.test(event.target.value)) {
      onChange(event);
    }
  };

  return (
    <Box sx={styles.quantityContainer}>
      {showDeleteButton ? (
        <Button
          disabled={disabled}
          variant="text"
          size="small"
          onClick={onDelete}
          sx={styles.quantityButtonDelete}
        >
          <SvgIcon fontSize="small">
            <TrashIcon />
          </SvgIcon>
        </Button>
      ) : (
        <Button
          disabled={disabled}
          variant="text"
          size="small"
          onClick={onMinus}
          sx={styles.quantityButtonMinus}
        >
          <SvgIcon fontSize="small">
            <MinusIcon />
          </SvgIcon>
        </Button>
      )}
      <TextField
        disabled={disabled}
        color="secondary"
        variant="outlined"
        fullWidth
        type="text"
        size="small"
        inputProps={{
          min,
          step: 1,
          inputMode: "numeric",
          pattern: "[0-9]*",
          readOnly: inputReadOnly,
        }}
        value={quantity === 0 ? "" : quantity}
        onChange={handleChange}
        onFocus={inputReadOnly ? undefined : handleSelectCurrentValue}
        onClick={inputReadOnly ? onQuantityClick : handleSelectCurrentValue}
        onMouseUp={handleMouseUp}
        sx={{
          ...styles.quantityInput,
          "& input": {
            ...styles.quantityInput["& input"],
            cursor: inputReadOnly ? "pointer" : "text",
          },
        }}
      />
      <Button
        disabled={disabled}
        variant="text"
        size="small"
        onClick={onPlus}
        sx={styles.quantityButtonPlus}
      >
        <SvgIcon fontSize="small">
          <PlusIcon />
        </SvgIcon>
      </Button>
    </Box>
  );
};

QuantitySelector.propTypes = {
  quantity: PropTypes.number.isRequired,
  onMinus: PropTypes.func.isRequired,
  onPlus: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  disabled: PropTypes.bool,
  min: PropTypes.number,
  inputReadOnly: PropTypes.bool,
  onQuantityClick: PropTypes.func,
};

export default QuantitySelector;

